import * as THREE from 'three'
import { SceneAuxiliaryObjectRegistry } from '../../scene-object'
import { SW_HOVER_COLOR } from '../render/sw-theme'
import {
  createOriginPoint,
  updateOriginPoint,
} from './build-origin-point'
import {
  createPlaneOutline,
  updatePlaneOutline,
} from './build-plane-outline'
import {
  resolveGlbDatumSlotFrame,
  type GlbDatumSlotTargetDescriptor,
} from './resolve-frame'
import { MEASURE_SELECTED_COLOR } from '../../material/MeasureSelectionMaterialFactory'
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent'

const DATUM_SLOT_SELECTED_COLOR = 0x63A8FF
const HOVER_GROUP_NAME = 'glb-datum-slot-hover-overlay'
const SELECTED_GROUP_NAME = 'glb-datum-slot-selected-overlay'
const MEASURE_GROUP_NAME = 'glb-datum-slot-measure-overlay'

/**
 * datum-slot 通道状态。
 * 说明：
 * 1. 每个通道固定持有平面与原点两个节点；
 * 2. 切换描述符时只更新节点内容与可见性，不再替换整棵 overlay 子树。
 */
interface GlbDatumSlotChannelState {
  /** 平面高亮节点。 */
  readonly planeOutline: THREE.LineSegments
  /** 原点高亮节点。 */
  readonly originPoint: THREE.Sprite
}

/**
 * GLB datum-slot 高亮渲染器。
 * 说明：
 * 1. 仅负责 datum-slot overlay 的 hover/selected 双通道管理；
 * 2. 不承载选择状态机，也不负责触发宿主重绘；
 * 3. 通道对象固定驻留于场景树，切换 descriptor 时只更新 frame、颜色与可见性；
 * 4. 渲染器销毁时再统一释放通道资源，避免生命周期中途替换节点引用。
 */
export class GlbDatumSlotHighlightRenderer {
  private readonly scene: THREE.Scene
  private readonly hoverGroup: THREE.Group
  private readonly selectedGroup: THREE.Group
  private readonly measureGroup: THREE.Group
  private readonly hoverChannel: GlbDatumSlotChannelState
  private readonly selectedChannel: GlbDatumSlotChannelState
  private readonly measureChannel: GlbDatumSlotChannelState

  /**
   * @param scene overlay 挂载的场景。
   */
  public constructor(scene: THREE.Scene) {
    this.scene = scene
    this.hoverGroup = this.createChannelGroup(HOVER_GROUP_NAME)
    this.selectedGroup = this.createChannelGroup(SELECTED_GROUP_NAME)
    this.measureGroup = this.createChannelGroup(MEASURE_GROUP_NAME)
    this.hoverChannel = this.createChannelState(this.hoverGroup, SW_HOVER_COLOR)
    this.selectedChannel = this.createChannelState(this.selectedGroup, DATUM_SLOT_SELECTED_COLOR)
    this.measureChannel = this.createChannelState(this.measureGroup, MEASURE_SELECTED_COLOR)
    addObject3DToParent(this.hoverGroup, this.scene)
    addObject3DToParent(this.selectedGroup, this.scene)
    addObject3DToParent(this.measureGroup, this.scene)
  }

  /**
   * 设置 hover 通道。
   *
   * @param descriptor hover 描述符；为空时清空通道。
   */
  public setHover(descriptor: GlbDatumSlotTargetDescriptor | null): void {
    this.updateChannel(this.hoverChannel, descriptor, SW_HOVER_COLOR)
  }

  /**
   * 设置 selected 通道。
   *
   * @param descriptor selected 描述符；为空时清空通道。
   */
  public setSelected(descriptor: GlbDatumSlotTargetDescriptor | null): void {
    this.updateChannel(this.selectedChannel, descriptor, DATUM_SLOT_SELECTED_COLOR)
  }

  /**
   * 设置测量模式 selected 通道（绿色平面线框 / 原点标记）。
   *
   * 设计说明：
   * 1. 与命令态蓝色 selected 通道独立，避免测量会话污染设计树选中语义；
   * 2. 复用与非测量模式相同的 frame 解析与矩形轮廓几何，仅颜色对齐测量绿。
   *
   * @param descriptor 测量选中 datum-slot 描述符；为空时清空通道。
   */
  public setMeasureSelected(descriptor: GlbDatumSlotTargetDescriptor | null): void {
    this.updateChannel(this.measureChannel, descriptor, MEASURE_SELECTED_COLOR)
  }

  /**
   * 释放渲染器资源。
   */
  public dispose(): void {
    this.disposeChannel(this.hoverChannel)
    this.disposeChannel(this.selectedChannel)
    this.disposeChannel(this.measureChannel)
    this.scene.remove(this.hoverGroup)
    this.scene.remove(this.selectedGroup)
    this.scene.remove(this.measureGroup)
  }

  /**
   * 创建 overlay 通道 group。
   *
   * @param name group 名称。
   * @returns 通道 group。
   */
  private createChannelGroup(name: string): THREE.Group {
    const group = new THREE.Group()
    // 仅标记显示通道，不改变真实基准槽位的业务选择身份。
    SceneAuxiliaryObjectRegistry.markRoot(group)
    group.name = name
    group.userData.isGlbDatumSlotOverlay = true
    return group
  }

  /**
   * 创建固定的 datum-slot 通道状态。
   * 说明：
   * 1. 这里先把平面与原点节点都挂到通道 group 上；
   * 2. 后续切换时只做节点更新，不再 remove + rebuild 整个 overlay。
   *
   * @param group 通道 group。
   * @param color 通道颜色。
   * @returns 通道状态。
   */
  private createChannelState(
    group: THREE.Group,
    color: number,
  ): GlbDatumSlotChannelState {
    const planeOutline = createPlaneOutline(color)
    const originPoint = createOriginPoint(color)

    planeOutline.visible = false
    originPoint.visible = false

  addObject3DToParent(planeOutline, group)
  addObject3DToParent(originPoint, group)

    return {
      planeOutline,
      originPoint,
    }
  }

  /**
   * 更新某个通道的节点内容。
   *
   * @param channel 通道状态。
   * @param descriptor datum-slot 描述符。
   * @param color overlay 颜色。
   */
  private updateChannel(
    channel: GlbDatumSlotChannelState,
    descriptor: GlbDatumSlotTargetDescriptor | null,
    color: number,
  ): void {
    if (!descriptor) {
      this.hideChannel(channel)
      return
    }

    const frame = resolveGlbDatumSlotFrame(this.scene, descriptor)
    if (!frame) {
      this.hideChannel(channel)
      return
    }

    if (frame.kind === 'plane') {
      updatePlaneOutline(channel.planeOutline, frame, color)
      channel.originPoint.visible = false
      return
    }

    updateOriginPoint(channel.originPoint, frame, color)
    channel.planeOutline.visible = false
  }

  /**
   * 隐藏某个通道的两个节点。
   *
   * @param channel 通道状态。
   */
  private hideChannel(channel: GlbDatumSlotChannelState): void {
    channel.planeOutline.visible = false
    channel.originPoint.visible = false
  }

  /**
   * 释放固定通道的资源。
   *
   * @param channel 通道状态。
   */
  private disposeChannel(channel: GlbDatumSlotChannelState): void {
    this.disposeObjectResources(channel.planeOutline)
    this.disposeObjectResources(channel.originPoint)
    channel.planeOutline.removeFromParent()
    channel.originPoint.removeFromParent()
  }

  /**
   * 递归释放 overlay 子树资源。
   *
   * @param object 要释放的对象。
   */
  private disposeObjectResources(object: THREE.Object3D): void {
    object.traverse((current) => {
      const geometry = Reflect.get(current, 'geometry') as THREE.BufferGeometry | undefined
      geometry?.dispose()

      const material = Reflect.get(current, 'material') as
        | THREE.Material
        | THREE.Material[]
        | undefined

      if (Array.isArray(material)) {
        material.forEach((item) => this.disposeMaterial(item))
        return
      }
      if (material) {
        this.disposeMaterial(material)
      }
    })
  }

  /**
   * 释放材质及其贴图。
   *
   * @param material 待释放材质。
   */
  private disposeMaterial(material: THREE.Material): void {
    const materialWithMap = material as THREE.Material & { map?: THREE.Texture | null }
    materialWithMap.map?.dispose()
    material.dispose()
  }
}
